import React, { useState } from "react"

import DineroFactory from "dinero.js"

import {
  ActionWrapper,
  CartItemContainer,
  InfoWrapper,
  ItemDetails,
  ItemImage,
  ItemName,
  ItemPrice,
  QtySelector
} from "./CartItem.styled"
import Money from "../../../../Money"

import placeholder from "../../../../../images/placeholder.svg"
import { OrderGift, OrderRefund, RefundGift } from "lib/types/generated/graphql-types"
import SecondaryButton from "../../../../Ui/Buttons/SecondaryButton"
import { ButtonWrapper } from "../Shared.styled"
import { useMutation } from "@apollo/client"
import UPDATE_REFUND from "../../../../../graphql/mutations/order/refunds/UpdateRefund"
import { Loader } from "../../../../Ui/Loader"
import { getRefundInput } from "../Helpers/helpers"
import { handleErrorMessages } from "helpers/errors"

type Props = {
  item: RefundGift
  currencyCode?: string
  orderGift: OrderGift | undefined
  refund: OrderRefund
  refetch: () => void
}

const GiftItem = ({ item, currencyCode, orderGift, refund, refetch, ...props }: Props) => {
  const [quantity, setQuantity] = useState(item.quantity)

  const [updateRefund, { loading }] = useMutation(UPDATE_REFUND, {
    onCompleted: () => refetch(),
    onError: (error) => {
      handleErrorMessages(error)
    }
  })

  const onUpdate = (qty: number) => {
      if (qty === 0) {
        return updateRefund({
          variables: {
            refundId: refund.id,
            input: {
              ...getRefundInput(refund),
              gifts: refund.gifts
                .filter((gift) => gift.giftId !== item.giftId)
                .map((gift) => ({ giftId: gift.giftId, quantity: gift.quantity }))
            }
          }
        })
      }
      return updateRefund({
        variables: {
          refundId: refund.id,
          input: {
            ...getRefundInput(refund),
            gifts: [
              ...refund.gifts.map((gift) => ({
                giftId: gift.giftId,
                quantity: gift.giftId === item.giftId ? qty : gift.quantity
              }))
            ]
          }
        }
      })
  }

  const updateQuantity = (mod: number) => {
    const qty = Math.max(quantity + mod, 0)
    setQuantity(qty)
    onUpdate(qty)
  }

  const handleImageError = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
    event.currentTarget.src = placeholder
  }

  return (
    <CartItemContainer {...props}>
      <InfoWrapper>
        <ItemImage>
          <img src={orderGift?.imageUrl ?? ""} onError={handleImageError} />
        </ItemImage>
        <ItemDetails>
          <ItemName>{orderGift?.name}</ItemName>
          <ItemPrice>
            <Money
              amount={Number(orderGift?.salePriceAmount)}
              currencyUnit={currencyCode as DineroFactory.Currency}
            />
            {orderGift && orderGift?.salePriceAmount < orderGift?.basePriceAmount && (
              <span>
                <Money
                  amount={Number(orderGift?.totalPriceAmount ?? 0)}
                  currencyUnit={currencyCode as DineroFactory.Currency}
                />
              </span>
            )}
          </ItemPrice>
        </ItemDetails>
      </InfoWrapper>

      <ActionWrapper>
        <span>
          <b>Action:</b>{" "}
          {orderGift?.quantity === item.quantity ? "Will be refunded" : "Will be refunded in part"}
        </span>
        <span>
          <b>Items to refund:</b>
          <QtySelector>
            {loading && <Loader color="black" />}
            <span onClick={() => updateQuantity(-1)}>-</span>
            <span>{item.quantity}</span>
            {
              <span
                onClick={() => {
                  if (orderGift?.quantity !== quantity) updateQuantity(+1)
                }}
              >
                +
              </span>
            }
          </QtySelector>
        </span>
        <span>
          <b>Amount to be refunded:</b>{" "}
          <Money
            amount={orderGift?.totalPriceAmount ?? 0}
            currencyUnit={currencyCode as DineroFactory.Currency}
          />
        </span>
        <hr />
        <ButtonWrapper>
          <SecondaryButton handleClick={() => onUpdate(0)}>Undo</SecondaryButton>
        </ButtonWrapper>
      </ActionWrapper>
    </CartItemContainer>
  )
}

export default GiftItem
